import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { user } from "../api/queries.client";
import { useUserFollowMutation } from "./use-user-follow-mutation";

interface UseUserFollowsOptions {
  userId: string;
  username: string;
  onSuccess?: (isFollowing: boolean) => void;
  onError?: (error: Error) => void;
}

export function useUserFollows({
  userId,
  username,
  onSuccess,
  onError,
}: UseUserFollowsOptions) {
  const { user: currentUser } = useAuth();
  const {
    mutate: toggleFollow,
    isPending: isMutating,
    error: mutationError,
  } = useUserFollowMutation({
    followeeId: userId,
    username,
    onSuccess,
    onError,
  });

  // Get the standardized query from queries.client.ts
  const followingInfoQuery = user.getFollowingInfo({
    userId,
    viewerId: currentUser?.uid,
  });

  // Query to get the current follow status - only for authenticated users
  const { data: followingInfo, isLoading: isLoadingFollowingInfo } = useQuery({
    ...followingInfoQuery,
    enabled: Boolean(userId) && Boolean(currentUser?.uid), // Only run for authenticated users
  });

  const isFollowing = followingInfo?.isFollowing ?? false;

  const handleToggleFollow = () => {
    if (!currentUser?.uid) return;

    toggleFollow({ shouldFollow: !isFollowing });
  };

  return {
    isFollowing,
    isLoading: isLoadingFollowingInfo,
    isMutating,
    toggleFollow: handleToggleFollow,
    followingInfo,
    error: mutationError,
  };
}
